import {
  FormDatePickerV2Input,
  FormInputController,
  FormNumberInputV2,
  FormSwitchInputV2,
  FormTextInputV2,
} from '@wajeez-web/components/form-builder';
import { Server } from '@wajeez/wajeez.microservices';
import { FieldValues, UseControllerProps } from 'react-hook-form';
import { Autocomplete } from '@wajeez-web/components/mui';
import { mediaMimeTypes } from '@wajeez-web/core/utils';
import { useContentSelector } from '@wajeez-web/stores/content';

import { FormInput } from './ContentEntityTypeFormBuilder';
import { SelectCreatorV2 } from '@wajeez-web/components/ui';

interface ContentEntityTypeFieldOptionsComponentsProps<
  FormInput extends FieldValues
> extends UseControllerProps<FormInput> {
  fieldType: Server.ContentEntityTypeFieldType | undefined;
  fieldIndex: number;
}

export const ContentEntityTypeFieldOptionsComponents = ({
  fieldType,
  fieldIndex,
  control,
}: ContentEntityTypeFieldOptionsComponentsProps<FormInput>) => {
  const componentTypesLookups = useContentSelector(
    ({ componentTypes }) => componentTypes.componentTypesLookups
  );

  const renderFieldOptionsComponents = () => {
    switch (fieldType) {
      case Server.ContentEntityTypeFieldType.Text:
        return (
          <>
            <FormNumberInputV2
              control={control}
              name={`fields.${fieldIndex}.fieldOptions.minLength`}
              fieldMeta={{
                fieldType: Server.ContentEntityTypeFieldType.Number,
                label: 'Min Length',
                placeHolder: 'Min Length',
                fieldOptions: { min: 1 },
              }}
            />
            <FormNumberInputV2
              control={control}
              name={`fields.${fieldIndex}.fieldOptions.maxLength`}
              fieldMeta={{
                fieldType: Server.ContentEntityTypeFieldType.Number,
                label: 'Max Length',
                placeHolder: 'Max Length',
                fieldOptions: { min: 1 },
              }}
            />
          </>
        );
      case Server.ContentEntityTypeFieldType.AreaText:
        return (
          <>
            <FormNumberInputV2
              control={control}
              name={`fields.${fieldIndex}.fieldOptions.minLength`}
              fieldMeta={{
                fieldType: Server.ContentEntityTypeFieldType.Number,
                label: 'Min Length',
                placeHolder: 'Min Length',
                fieldOptions: { min: 1 },
              }}
            />
            <FormNumberInputV2
              control={control}
              name={`fields.${fieldIndex}.fieldOptions.maxLength`}
              fieldMeta={{
                fieldType: Server.ContentEntityTypeFieldType.Number,
                label: 'Max Length',
                placeHolder: 'Max Length',
                fieldOptions: { min: 1 },
              }}
            />
            <FormNumberInputV2
              control={control}
              name={`fields.${fieldIndex}.fieldOptions.rows`}
              fieldMeta={{
                fieldType: Server.ContentEntityTypeFieldType.Number,
                label: 'Rows',
                placeHolder: 'Rows',
                fieldOptions: { min: 1 },
              }}
            />
          </>
        );
      case Server.ContentEntityTypeFieldType.TineMCE:
        return (
          <>
            <FormNumberInputV2
              control={control}
              name={`fields.${fieldIndex}.fieldOptions.minLength`}
              fieldMeta={{
                fieldType: Server.ContentEntityTypeFieldType.Number,
                label: 'Min Length',
                placeHolder: 'Min Length',
                fieldOptions: { min: 1 },
              }}
            />
            <FormNumberInputV2
              control={control}
              name={`fields.${fieldIndex}.fieldOptions.maxLength`}
              fieldMeta={{
                fieldType: Server.ContentEntityTypeFieldType.Number,
                label: 'Max Length',
                placeHolder: 'Max Length',
                fieldOptions: { min: 1 },
              }}
            />
            <FormNumberInputV2
              control={control}
              name={`fields.${fieldIndex}.fieldOptions.height`}
              fieldMeta={{
                fieldType: Server.ContentEntityTypeFieldType.Number,
                label: 'Height',
                placeHolder: 'Height',
                fieldOptions: { min: 1 },
              }}
            />
          </>
        );
      case Server.ContentEntityTypeFieldType.Date:
      case Server.ContentEntityTypeFieldType.DateTime:
        return (
          <>
            <FormDatePickerV2Input
              control={control}
              name={`fields.${fieldIndex}.fieldOptions.minDate`}
              fieldMeta={{
                fieldType: Server.ContentEntityTypeFieldType.DateTime,
                label: 'Min Date',
                placeHolder: 'Min Date',
                fieldOptions: {},
              }}
            />
            <FormDatePickerV2Input
              control={control}
              name={`fields.${fieldIndex}.fieldOptions.maxDate`}
              fieldMeta={{
                fieldType: Server.ContentEntityTypeFieldType.DateTime,
                label: 'Max Date',
                placeHolder: 'Max Date',
                fieldOptions: {},
              }}
            />
          </>
        );
      case Server.ContentEntityTypeFieldType.DragFile:
      case Server.ContentEntityTypeFieldType.UploadFile:
      case Server.ContentEntityTypeFieldType.UploadImage:
        return (
          <>
            <FormNumberInputV2
              control={control}
              name={`fields.${fieldIndex}.fieldOptions.upToKB`}
              fieldMeta={{
                fieldType: Server.ContentEntityTypeFieldType.Number,
                label: 'Size Up To (KB)',
                placeHolder: 'Size Up To (KB)',
                fieldOptions: {},
              }}
            />
            <FormTextInputV2
              control={control}
              name={`fields.${fieldIndex}.fieldOptions.uploadEndPoint`}
              fieldMeta={{
                fieldType: Server.ContentEntityTypeFieldType.Text,
                label: 'Upload Endpoint',
                placeHolder: 'Upload Endpoint',
                fieldOptions: {},
              }}
            />
            <div className="col-span-2">
              <FormInputController
                control={control}
                name={`fields.${fieldIndex}.fieldOptions.types`}
                fieldMeta={{
                  fieldType: Server.ContentEntityTypeFieldType.Select,
                  fieldOptions: {},
                }}
                renderInput={({ ...props }) => (
                  <div className="h-fit">
                    <Autocomplete
                      id={`types-autocomplete-${fieldIndex}`}
                      options={mediaMimeTypes.map((m) => {
                        return {
                          value: m,
                          label: m,
                        };
                      })}
                      label="Types"
                      color="#1f7983"
                      heightClassName="h-full"
                      multiple
                      {...props}
                    />
                  </div>
                )}
              />
            </div>
          </>
        );
      case Server.ContentEntityTypeFieldType.CallbackSelect:
        return (
          <>
            <FormSwitchInputV2
              control={control}
              name={`fields.${fieldIndex}.fieldOptions.isMulti`}
              fieldMeta={{
                fieldType: Server.ContentEntityTypeFieldType.Switch,
                label: 'Is Multi?',
                fieldOptions: {},
              }}
            />
            <FormSwitchInputV2
              control={control}
              name={`fields.${fieldIndex}.fieldOptions.withOrder`}
              fieldMeta={{
                fieldType: Server.ContentEntityTypeFieldType.Switch,
                label: 'With Order?',
                fieldOptions: {},
              }}
            />
            <FormTextInputV2
              control={control}
              name={`fields.${fieldIndex}.fieldOptions.optionsListEndPoint`}
              fieldMeta={{
                fieldType: Server.ContentEntityTypeFieldType.Text,
                label: 'Options List Endpoint',
                placeHolder: 'Options List Endpoint',
                fieldOptions: {},
              }}
            />
          </>
        );
      case Server.ContentEntityTypeFieldType.Select:
        return (
          <>
            <FormSwitchInputV2
              control={control}
              name={`fields.${fieldIndex}.fieldOptions.isMulti`}
              fieldMeta={{
                fieldType: Server.ContentEntityTypeFieldType.Switch,
                label: 'Is Multi?',
                fieldOptions: {},
              }}
            />
            <FormSwitchInputV2
              control={control}
              name={`fields.${fieldIndex}.fieldOptions.withOrder`}
              fieldMeta={{
                fieldType: Server.ContentEntityTypeFieldType.Switch,
                label: 'With Order?',
                fieldOptions: {},
              }}
            />
            <div className="col-span-2">
              <FormInputController
                name={`fields.${fieldIndex}.fieldOptions.selectOptions`}
                control={control}
                fieldMeta={{
                  fieldType: Server.ContentEntityTypeFieldType.Select,
                }}
                renderInput={({ onChange }) => {
                  return (
                    <SelectCreatorV2
                      name={`fields.${fieldIndex}.fieldOptions.selectOptions`}
                      label="Select Options"
                      onChange={(value) => {
                        onChange(value);
                      }}
                    />
                  );
                }}
              />
            </div>
          </>
        );
      case Server.ContentEntityTypeFieldType.Number:
        return (
          <>
            <FormNumberInputV2
              control={control}
              name={`fields.${fieldIndex}.fieldOptions.min`}
              fieldMeta={{
                fieldType: Server.ContentEntityTypeFieldType.Number,
                label: 'Min',
                placeHolder: 'Min',
                fieldOptions: {},
              }}
            />
            <FormNumberInputV2
              control={control}
              name={`fields.${fieldIndex}.fieldOptions.max`}
              fieldMeta={{
                fieldType: Server.ContentEntityTypeFieldType.Number,
                label: 'Max',
                placeHolder: 'Max',
                fieldOptions: {},
              }}
            />
          </>
        );
      default:
        return null;
    }
  };

  return <>{renderFieldOptionsComponents()}</>;
};
